import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: ReactNode;
  /** Rendered inside the field on the trailing edge — e.g. a show/hide toggle. */
  trailingElement?: ReactNode;
  size?: "md" | "lg";
}

/**
 * Text field with a floating label.
 *
 * The label animation is pure CSS: `placeholder=" "` (one space) makes
 * `:placeholder-shown` true only while the field is empty, and the label reads
 * that through Tailwind's `peer-` variants. No focus state in React, so the
 * label can never desynchronise from the input's actual state.
 *
 * `outline-none` is safe here only because the global `:focus-visible` rule in
 * index.css supplies a ring for every focusable element. Removing an outline
 * without a replacement locks out keyboard users.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, icon, trailingElement, size = "md", className = "", id, required, ...props },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const heightClass = size === "lg" ? "h-12" : "h-11";

  const borderState = error
    ? "border-danger focus:border-danger"
    : "border-line-strong focus:border-accent-500";

  // Without a label this is a bare control, for dense inline use.
  if (!label) {
    return (
      <input
        ref={ref}
        id={inputId}
        required={required}
        className={`w-full rounded-lg border bg-card px-3 text-sm text-ink-900 outline-none transition-colors
          placeholder:text-muted-subtle
          ${borderState}
          disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-subtle
          ${heightClass} ${className}`}
        aria-invalid={Boolean(error)}
        {...props}
      />
    );
  }

  return (
    <div className="w-full">
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-subtle">
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          placeholder=" "
          required={required}
          className={`peer w-full rounded-lg border bg-card text-sm text-ink-900 outline-none transition-colors
            ${icon ? "ps-9" : "ps-3"} ${trailingElement ? "pe-9" : "pe-3"}
            ${heightClass} pt-3
            ${borderState}
            disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-subtle
            ${className}`}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={`pointer-events-none absolute ${icon ? "start-9" : "start-3"} top-1.5 text-[11px] text-muted-foreground transition-all
            peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-muted-subtle
            peer-focus:top-1.5 peer-focus:text-[11px] peer-focus:text-accent-600`}
        >
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
        {trailingElement && (
          <div className="absolute inset-y-0 end-3 flex items-center">{trailingElement}</div>
        )}
      </div>

      {error ? (
        <p id={`${inputId}-error`} className="mt-1.5 flex items-center gap-1 text-xs text-danger">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : helperText ? (
        <p id={`${inputId}-helper`} className="mt-1.5 text-xs text-muted-foreground">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});

/** Static label, for controls that cannot host a floating one (textarea, colour, file). */
export function Label({ children }: { children: ReactNode }) {
  return <label className="mb-1.5 block text-xs font-medium text-ink-600">{children}</label>;
}
